import { cn } from '@modules/lib/lib'

type Props = {
  /** PDF の URL。blob: / data:application/pdf / 署名付き URL いずれも可 */
  src: string
  /** スクリーンリーダー向けのラベル。ファイル名を含めると分かりやすい */
  ariaLabel: string
  /** 表示できなかったときに添える補足（ファイル名など） */
  fallbackDetail?: string
  className?: string
}

/**
 * `<object data>` に埋め込んでよい URL だけを通す。
 *
 * `data` に渡した URL は、`type` の指定に関わらずブラウザがスキーム優先で解釈するため、
 * `data:text/html,<script>…` や `javascript:` を渡されると HTML として実行されうる
 * （CodeQL `js/xss-through-dom`）。現状ここに来るのは署名付き URL と `URL.createObjectURL`
 * の戻り値だけだが、呼び出し側が増えても XSS にならないようコンポーネント側で閉じる。
 *
 * 判定はスキームの許可リストで行う。`data:` は `URL` の `protocol` からは MIME まで
 * 分からないので、`application/pdf` であることを文字列として検査する。
 */
const toEmbeddableSrc = (src: string): string | undefined => {
  if (/^data:application\/pdf[;,]/i.test(src)) return src

  try {
    // 基底 URL を渡さない＝絶対 URL だけを受け付ける。ここに来るのは署名付き URL と
    // blob URL だけなので、相対パスを自オリジンとして解決する余地は持たせない
    const url = new URL(src)
    if (url.protocol !== 'http:' && url.protocol !== 'https:' && url.protocol !== 'blob:') {
      return undefined
    }
    return url.href
  } catch {
    // URL として解釈できない文字列は埋め込まない
    return undefined
  }
}

/**
 * PDF をページ全体が見える状態で埋め込む。
 *
 * pdf.js 等のビューアライブラリは入れず、ブラウザ内蔵のビューアに委ねる。
 * `#view=Fit` を付けるのは、付けないと先頭の一部しか見えず「何の書類か」が
 * 判断できないため（issue #2489 のレビュー指摘）。
 *
 * `<object>` は表示できない場合に子要素へフォールバックする。iframe と違い
 * 「表示できなかった」ことを利用者に伝えられるのでこちらを使う。埋め込めない
 * URL（許可していないスキーム）を渡されたときも、同じフォールバックを出す。
 */
export const PdfPreview: React.FC<Props> = ({ src, ariaLabel, fallbackDetail, className }) => {
  const embeddable = toEmbeddableSrc(src)

  const fallback = (
    <div className="flex h-64 flex-col items-center justify-center gap-2 text-slate-400">
      <p className="text-sm font-medium">
        PDFプレビューを表示できません（ご利用のブラウザが未対応の可能性があります）
      </p>
      {fallbackDetail && <p className="text-sm">{fallbackDetail}</p>}
    </div>
  )

  if (embeddable === undefined) return fallback

  return (
    <object
      aria-label={ariaLabel}
      className={cn('h-[70vh] max-h-[720px] w-full', className)}
      data={`${embeddable}#view=Fit`}
      type="application/pdf"
    >
      {fallback}
    </object>
  )
}
